/**
 * Extracts a unique set of responsible users from the hit results based on their employee Uno.
 * Matches the employee IDs in the hit results with the provided list of all users and returns
 * their details (id, name, email, and employeeId).
 *
 * @param hitResults - The hit results containing request terms and hits.
 * @param allUsers - The list of all users to match against.
 * @returns A promise that resolves to an array of unique ReviewerInfo objects.
 */

import { hasRole, UserRole } from "aderant-conflicts-models";

export type ReviewerInfo = {
    id: string;
    name: string;
    email: string;
    employeeId: string;
};

export async function extractUniqueReviewersFromHitResults(hitResults: any, allUsers: any[]): Promise<ReviewerInfo[]> {
    //const reviewerSet = new Set<ReviewerInfo>(); // Use a Set to store unique emails
    const reviewerMap = new Map<string, ReviewerInfo>(); // Use a Map to ensure uniqueness by employeeId
    const userMap = new Map(allUsers.map((user) => [user.employeeId, user])); // Pre-index users by employeeId for faster lookups

    if (!hitResults || typeof hitResults !== "object") {
        throw new Error("Invalid hitResults object");
    }

    if (!hitResults?.requestTerms || !Array.isArray(hitResults.requestTerms)) {
        return Array.from(reviewerMap.values()); // Return an empty array if requestTerms is not valid
    }

    for (const term of hitResults.requestTerms) {
        if (!term?.hits || !Array.isArray(term.hits)) {
            continue; // Skip if hits are not valid
        }
        for (const hit of term.hits) {
            const responsibleEmployeeId = hit?.sourceData?.responsibleEmployeeId;
            if (responsibleEmployeeId && userMap.has(responsibleEmployeeId)) {
                const matchedUser = userMap.get(responsibleEmployeeId);
                if (matchedUser && hasRole(matchedUser.role, UserRole.Reviewer)) {
                    reviewerMap.set(responsibleEmployeeId, {
                        id: matchedUser.id,
                        name: matchedUser.name,
                        email: matchedUser.email,
                        employeeId: matchedUser.employeeId
                    });
                }
            }
        }
    }

    return Array.from(reviewerMap.values()); // Convert the Map back to an array
}
